import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function AuthModal({ open, onClose, onSuccess, message, initialMode = "login" }) {
  const { login, signup, verifyOtp, resendOtp } = useAuth();
  const [mode, setMode] = useState(initialMode); // login | register | verify
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", channel: "sms" });
  const [code, setCode] = useState("");
  const [sentChannel, setSentChannel] = useState("sms");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError("");
      setInfo(initialMode === "verify" ? "Enter the code we sent you to finish verifying." : "");
      setCode("");
      setBusy(false);
    }
  }, [open, initialMode]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finishIfConfirmed = (user) => {
    if (user?.confirmed) {
      onSuccess ? onSuccess() : onClose();
    } else {
      setSentChannel(form.channel);
      setInfo(`We sent a 6-digit code to your ${form.channel === "email" ? "email" : "phone"}.`);
      setMode("verify");
    }
  };

  const doLogin = async () => {
    const res = await login({ email: form.email, password: form.password });
    if (res.ok) finishIfConfirmed(res.data.user);
    else setError(res.data.error || "Could not sign in.");
  };

  const doRegister = async () => {
    if (!form.name || !form.phone || !form.email || !form.password) {
      setError("Please fill in your name, phone, email and password.");
      return false;
    }
    const res = await signup(form);
    if (res.ok) {
      setSentChannel(res.data.channel || form.channel);
      setInfo(`We sent a 6-digit code to your ${(res.data.channel || form.channel) === "email" ? "email" : "phone"}.`);
      setMode("verify");
    } else {
      setError(res.data.error || "Could not create your account.");
    }
  };

  const doVerify = async () => {
    const res = await verifyOtp(code.trim());
    if (res.ok) {
      onSuccess ? onSuccess() : onClose();
    } else {
      setError(res.data.error || "Verification failed.");
    }
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    if (mode === "login") await doLogin();
    else if (mode === "register") await doRegister();
    else await doVerify();
    setBusy(false);
  };

  const resend = async (channel) => {
    setError("");
    const res = await resendOtp(channel);
    if (res.ok) {
      setSentChannel(channel);
      setInfo(`A new code was sent to your ${channel === "email" ? "email" : "phone"}.`);
    } else {
      setError(res.data.error || "Could not resend the code.");
    }
  };

  const titles = {
    login: "Welcome back",
    register: "Create your account",
    verify: "Verify your account",
  };

  return (
    <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: "440px" }}>
        <div className="modal-hero">
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
          <div className="ic">
            <i className={`fa-solid ${mode === "verify" ? "fa-shield-halved" : "fa-user"}`} />
          </div>
          <h2>{titles[mode]}</h2>
          <p>{mode === "verify" ? info || "Enter the 6-digit code we sent you." : message || "Sign in to manage your orders and check out."}</p>
        </div>

        <div className="modal-body">
          {error && <div className="field-error" style={{ marginBottom: "12px" }}>{error}</div>}
          {mode !== "verify" && info && (
            <div style={{ marginBottom: "12px", color: "var(--leaf)", fontWeight: 600, fontSize: ".88rem" }}>{info}</div>
          )}

          {mode === "register" && (
            <>
              <div className="fg">
                <label>Full name</label>
                <input type="text" value={form.name} onChange={set("name")} placeholder="Your name" />
              </div>
              <div className="fg">
                <label>Phone (M-Pesa) *</label>
                <input type="tel" value={form.phone} onChange={set("phone")} placeholder="07xx xxx xxx" />
              </div>
            </>
          )}

          {mode !== "verify" && (
            <>
              <div className="fg">
                <label>Email</label>
                <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
              </div>
              <div className="fg">
                <label>Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={set("password")}
                  placeholder="At least 6 characters"
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                />
              </div>
            </>
          )}

          {mode === "register" && (
            <div className="fg">
              <label>Where should we send your code?</label>
              <div className="channel-choice">
                <label className={form.channel === "sms" ? "active" : ""}>
                  <input type="radio" name="channel" checked={form.channel === "sms"} onChange={() => setForm((f) => ({ ...f, channel: "sms" }))} />
                  <i className="fa-solid fa-comment-sms" /> Text my phone
                </label>
                <label className={form.channel === "email" ? "active" : ""}>
                  <input type="radio" name="channel" checked={form.channel === "email"} onChange={() => setForm((f) => ({ ...f, channel: "email" }))} />
                  <i className="fa-solid fa-envelope" /> Email me
                </label>
              </div>
            </div>
          )}

          {mode === "verify" && (
            <div className="fg">
              <label>6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                style={{ letterSpacing: ".4em", fontSize: "1.2rem", textAlign: "center" }}
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
            </div>
          )}

          <div className="checkout-actions">
            <button className="btn btn-leaf" onClick={submit} disabled={busy}>
              {busy ? "Please wait…" : mode === "login" ? "Log in" : mode === "register" ? "Create account" : "Verify"}
            </button>
          </div>

          {mode === "verify" ? (
            <p style={{ textAlign: "center", marginTop: "16px", fontSize: ".84rem", color: "var(--muted)" }}>
              Didn't get it?{" "}
              <a href="#" style={{ color: "var(--leaf)", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); resend(sentChannel); }}>
                Resend
              </a>{" "}
              ·{" "}
              <a href="#" style={{ color: "var(--leaf)", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); resend(sentChannel === "sms" ? "email" : "sms"); }}>
                Send via {sentChannel === "sms" ? "email" : "SMS"} instead
              </a>
            </p>
          ) : (
            <p style={{ textAlign: "center", marginTop: "16px", fontSize: ".88rem", color: "var(--muted)" }}>
              {mode === "login" ? "New here? " : "Already have an account? "}
              <a
                href="#"
                style={{ color: "var(--leaf)", fontWeight: 700 }}
                onClick={(e) => {
                  e.preventDefault();
                  setError("");
                  setInfo("");
                  setMode(mode === "login" ? "register" : "login");
                }}
              >
                {mode === "login" ? "Create an account" : "Log in"}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
